import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Home, ArrowLeft, Search, Tv, Film, HelpCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  const popularLinks = [
    { href: "/", label: "Accueil", icon: Home, description: "Retour à la page principale" },
    { href: "/nos-offres", label: "Nos Offres", icon: Tv, description: "Découvrir nos abonnements" },
    { href: "/tutoriels", label: "Tutoriels", icon: BookOpen, description: "Guides d'installation" },
    { href: "/avis-clients", label: "Avis Clients", icon: Film, description: "Ce que disent nos clients" },
  ];

  const seoLinks = [
    { href: "/iptv-france", label: "IPTV France" },
    { href: "/abonnement-iptv", label: "Abonnement IPTV" },
    { href: "/iptv-pas-cher", label: "IPTV Pas Cher" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Page non trouvée - RoiDuStreaming</title>
        <meta name="description" content="La page que vous recherchez n'existe pas. Découvrez nos offres IPTV premium avec +15 000 chaînes TV, films et séries." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Header />

      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          {/* Emoji animé */}
          <div className="text-8xl mb-6 animate-bounce">🔍</div>
          
          {/* Titre principal */}
          <h1 className="text-5xl md:text-7xl font-black mb-4">
            <span className="gradient-text">404</span>
          </h1>
          
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Oups ! Page introuvable
          </h2>
          
          <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
            La page <code className="bg-muted px-2 py-1 rounded text-primary text-sm">{location.pathname}</code> n'existe pas ou a été déplacée.
          </p>

          {/* Boutons principaux */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/">
              <Button 
                size="lg" 
                className="btn-primary text-primary-foreground gap-2 w-full sm:w-auto"
                aria-label="Retourner à la page d'accueil"
              >
                <Home className="w-5 h-5" aria-hidden="true" />
                Retour à l'accueil
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => window.history.back()}
              className="gap-2 border-primary/50"
              aria-label="Revenir à la page précédente"
            >
              <ArrowLeft className="w-5 h-5" aria-hidden="true" />
              Page précédente
            </Button>
          </div>

          {/* Liens populaires */}
          <div className="card-premium p-6 rounded-2xl mb-8">
            <h3 className="text-lg font-bold text-foreground mb-4 flex items-center justify-center gap-2">
              <Search className="w-5 h-5 text-primary" aria-hidden="true" />
              Pages populaires
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {popularLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex flex-col items-center p-4 rounded-xl bg-background hover:bg-primary/10 transition-all duration-200 group"
                  aria-label={link.description}
                >
                  <link.icon className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span className="text-sm font-medium text-foreground">{link.label}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Liens SEO */}
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">Vous cherchiez peut-être :</p>
            <div className="flex flex-wrap justify-center gap-2">
              {seoLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-primary hover:underline"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Message d'aide */}
          <div className="mt-8 p-4 bg-primary/10 rounded-xl border border-primary/20">
            <div className="flex items-center justify-center gap-2 text-primary">
              <HelpCircle className="w-5 h-5" aria-hidden="true" />
              <span className="font-medium">Besoin d'aide ?</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Contactez-nous via le bouton en bas à droite de l'écran.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
